/**
 * What a video section knows about videos.
 *
 * Three callers need the same answers and would each get them slightly wrong
 * on their own: the inspector, which has to tell a merchant their link is not
 * a YouTube link *while they are still typing it*; the renderer, which has to
 * turn that link into something an iframe can load; and the schema, which has
 * to agree with the server about what it will accept. Mirrors
 * `youtube_video_id` and the `VideoSection` field rules in
 * `app/schemas/store.py` - the server is what validates a save, so a
 * disagreement here shows up as a section the customiser accepted and the API
 * refused.
 */

/**
 * Every shape of YouTube link a merchant might paste.
 *
 * The share sheet gives `youtu.be`, the address bar gives `watch?v=`, the
 * embed dialog gives `embed/`, a phone gives `m.` or `shorts/`, and a stream
 * gives `live/`. All of them are the same video, so refusing any one of them
 * would read as the field being broken rather than the link being wrong.
 *
 * The id is eleven characters of `[A-Za-z0-9_-]`, which is what lets the
 * trailing `?t=90` or `&list=...` be recognised as leftovers rather than swept
 * into the id.
 */
const YOUTUBE_URL =
  /^https?:\/\/(?:www\.|m\.)?(?:youtube\.com\/(?:watch\?(?:[^\s]*&)?v=|embed\/|v\/|shorts\/|live\/)|youtube-nocookie\.com\/embed\/|youtu\.be\/)([A-Za-z0-9_-]{11})(?:[?&/#][^\s]*)?$/i;

/** The video a YouTube link points at, or `''` if it points at nothing. */
export function youtubeVideoId(value: string | null | undefined): string {
  const match = YOUTUBE_URL.exec((value ?? '').trim());
  return match ? match[1] : '';
}

/**
 * Whether a merchant's half-typed link should be complained about yet.
 *
 * Empty is not an error - a section is added before it is filled in, and a
 * field that turns red the moment it is cleared is a field that nags. Anything
 * else either resolves to a video or does not.
 */
export function isYoutubeUrlValid(value: string | null | undefined): boolean {
  const trimmed = (value ?? '').trim();
  return trimmed === '' || youtubeVideoId(trimmed) !== '';
}

/**
 * The embed URL for one video, with the merchant's settings folded in.
 *
 * `youtube-nocookie.com` rather than `youtube.com`: a shop that has not asked
 * its shopper about tracking should not set an advertising cookie on their
 * behalf just because there is a video on the page. It plays identically.
 *
 * Two of these parameters exist to work around YouTube rather than to express
 * a setting. `mute=1` is forced under autoplay because every browser blocks an
 * unmuted autoplay, and a merchant should learn that from this code rather
 * than from their own page sitting still. And a single-video loop needs
 * `playlist` set to that same video - without it `loop=1` is silently ignored,
 * which is the kind of thing that gets reported as "the loop toggle does
 * nothing".
 */
export function youtubeEmbedUrl(
  videoId: string,
  options: { autoplay: boolean; loop: boolean; muted: boolean },
): string {
  const params = new URLSearchParams({
    // Play, pause, seek, volume and fullscreen. Not a setting: a video a
    // shopper cannot stop is a thing done to them.
    controls: '1',
    // Related videos from this channel only, rather than the whole of YouTube
    // suggesting a competitor on a shop's own page.
    rel: '0',
    playsinline: '1',
  });

  if (options.autoplay) params.set('autoplay', '1');
  if (options.muted || options.autoplay) params.set('mute', '1');
  if (options.loop) {
    params.set('loop', '1');
    params.set('playlist', videoId);
  }

  return `https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`;
}

/**
 * The shapes the player may be drawn at, and what a merchant calls them.
 *
 * Named rather than measured, for the same reason padding is: the renderer
 * owns the pixels, so a merchant cannot leave a player two pixels tall. It is
 * the *frame* that is fixed and not the video - a widescreen film in a
 * portrait frame is letterboxed inside it rather than stretched - which is
 * what makes this safe to offer as a free choice.
 */
export const VIDEO_ASPECT_RATIOS = ['16:9', '4:3', '1:1', '9:16'] as const;

export type VideoAspectRatio = (typeof VIDEO_ASPECT_RATIOS)[number];

/**
 * Where a video's own words stand over its frame. `none` - the default, and
 * every video saved before this existed - is the plain player. Mirrors
 * `VideoOverlayPosition` in `app/schemas/store.py`.
 */
export const VIDEO_OVERLAY_POSITIONS = ['none', 'bottom-left', 'center', 'bottom-center'] as const;

export type VideoOverlayPosition = (typeof VIDEO_OVERLAY_POSITIONS)[number];

export const VIDEO_ASPECT_LABELS: Record<VideoAspectRatio, string> = {
  '16:9': 'Widescreen (16:9)',
  '4:3': 'Classic (4:3)',
  '1:1': 'Square (1:1)',
  '9:16': 'Portrait (9:16)',
};

/**
 * The CSS ratio each one is drawn at.
 *
 * A plain `aspect-ratio` on a wrapper the player fills absolutely, rather than
 * a fixed height: the frame then shrinks with the column it is in, which is
 * the whole of what "responsive" means for a video and is the same on a
 * desktop, a tablet and a phone without a breakpoint being named.
 */
export const VIDEO_ASPECT_CSS: Record<VideoAspectRatio, string> = {
  '16:9': '16 / 9',
  '4:3': '4 / 3',
  '1:1': '1 / 1',
  '9:16': '9 / 16',
};

/** Where a section's video comes from. Two sources, two different players. */
export const VIDEO_SOURCES = ['youtube', 'upload'] as const;

export type VideoSource = (typeof VIDEO_SOURCES)[number];

/**
 * The formats the upload endpoint accepts, for the file picker's filter.
 *
 * Matches `VIDEO_TYPES` in `services/media/service.py`. Short on purpose:
 * nothing on the server transcodes, so a format only some browsers decode
 * would be stored happily and then fail to play for half the shop's visitors.
 */
export const VIDEO_ACCEPT = 'video/mp4,video/webm,video/ogg';
